import { z } from "zod";
import { WorkflowTraceParentSchema, WorkflowTraceStateSchema } from "./WorkflowQueueSchema";

export const OutboundTransportSchema = z.enum([
  "PLATFORM",
  "SMTP",
  "MICROSOFT_GRAPH_DELEGATED",
  "MICROSOFT_GRAPH_APPLICATION",
]);
export type OutboundTransport = z.infer<typeof OutboundTransportSchema>;

export const OutboundDeliveryProviderSchema = z.enum([
  "SMTP",
  "MICROSOFT_GRAPH",
  "SENDGRID",
  "SYSTEM",
  "CLOUDFLARE",
  "BREVO",
  "RESEND",
]);
export type OutboundDeliveryProvider = z.infer<typeof OutboundDeliveryProviderSchema>;

/**
 * Cloudflare Email Routing Configuration
 * Enables dynamic email address creation per tenant via Cloudflare Email Routing API
 */
export const CloudflareEmailRoutingConfigSchema = z.object({
  enabled: z.boolean().default(false),
  zoneId: z.string().trim().min(1).optional(), // Cloudflare Zone ID for trivoa.app
  accountId: z.string().trim().optional(), // Cloudflare Account ID
  routingRuleId: z.string().trim().optional(), // ID of the email routing rule in Cloudflare
  destinationAddress: z.string().email().optional(), // Where emails are forwarded to (our worker)
  customDomain: z.string().trim().optional(), // Custom domain for tenant (e.g., invoices.acme.com)
  customDomainVerified: z.boolean().default(false),
  createdAt: z.string().datetime().optional(),
  updatedAt: z.string().datetime().optional(),
});

export type CloudflareEmailRoutingConfig = z.infer<typeof CloudflareEmailRoutingConfigSchema>;

/**
 * Email Address Configuration for a Tenant
 */
export const TenantEmailConfigSchema = z.object({
  // Inbound email configuration
  inboundEnabled: z.boolean().default(true),
  primaryInboxAddress: z.string().email(), // Auto-generated: inbox.{tenant_id}@trivoa.app
  customInboxAddresses: z.array(z.string().email()).default([]), // Additional aliases

  // Outbound email configuration
  outboundEnabled: z.boolean().default(true),
  outboundTransport: OutboundTransportSchema.default("PLATFORM"),
  defaultFromName: z.string().trim().optional(),
  defaultFromEmail: z.string().email().optional(),
  replyToEmail: z.string().email().optional(),

  // Email templates
  invoiceEmailTemplate: z.string().optional(),
  dunningEmailTemplate: z.string().optional(),
  reminderEmailTemplate: z.string().optional(),

  // Signature
  emailSignature: z.string().optional(),
  includeLogoInEmails: z.boolean().default(true),
});

export type TenantEmailConfig = z.infer<typeof TenantEmailConfigSchema>;

/**
 * Outbound Email Record - Tracks all sent emails for audit purposes
 */
export const OutboundEmailRecordSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  documentId: z.string().uuid().optional(), // Related invoice/dunning

  // Email details
  fromEmail: z.string().email(),
  toEmail: z.string().email(),
  ccEmails: z.array(z.string().email()).default([]),
  bccEmails: z.array(z.string().email()).default([]),
  subject: z.string(),
  bodyHtml: z.string().optional(),
  bodyText: z.string().optional(),

  // Attachments
  attachments: z.array(z.object({
    filename: z.string(),
    contentType: z.string(),
    size: z.number(),
    r2Key: z.string().optional(), // Reference to R2 storage
  })).default([]),

  // Delivery status
  status: z.enum(["DRAFT", "QUEUED", "SENDING", "SENT", "DELIVERED", "BOUNCED", "FAILED", "DELIVERY_UNKNOWN"]),
  sentAt: z.string().datetime().optional(),
  deliveredAt: z.string().datetime().optional(),
  bouncedAt: z.string().datetime().optional(),
  bounceReason: z.string().optional(),

  // Provider info
  provider: OutboundDeliveryProviderSchema,
  providerMessageId: z.string().optional(),
  selectedTransport: OutboundTransportSchema.optional(),
  selectedProviderId: z.string().uuid().optional(),
  providerSnapshotKey: z.string().trim().min(1).max(128).optional(),
  idempotencyKey: z.string().trim().min(1).max(256).optional(),
  traceparent: WorkflowTraceParentSchema.optional(),
  tracestate: WorkflowTraceStateSchema.optional(),
  attemptCount: z.number().int().nonnegative().default(0),
  maxAttempts: z.number().int().min(1).max(10).default(5),
  nextAttemptAt: z.string().datetime().optional(),
  leaseExpiresAt: z.string().datetime().optional(),
  claimToken: z.string().uuid().optional(),
  lastErrorCode: z.string().trim().max(128).optional(),

  // Compliance archive links (separate from mailbox folder archive)
  archiveEntryIds: z.array(z.string().uuid()).default([]),
  deliveryArchiveEntryId: z.string().uuid().optional(),
  complianceArchived: z.boolean().default(false),

  // Audit
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type OutboundEmailRecord = z.infer<typeof OutboundEmailRecordSchema>;

export const OutboundMailQueueMessageSchema = z.object({
  version: z.literal(1),
  type: z.literal("OUTBOUND_MAIL_SEND"),
  messageId: z.string().uuid(),
  outboxId: z.string().trim().min(1).max(256),
  tenantId: z.string().trim().min(1).max(256),
  idempotencyKey: z.string().trim().min(1).max(256),
  traceparent: WorkflowTraceParentSchema,
  tracestate: WorkflowTraceStateSchema.optional(),
  enqueuedAt: z.string().datetime(),
}).strict();

export type OutboundMailQueueMessage = z.infer<typeof OutboundMailQueueMessageSchema>;

/**
 * Inbound Email Record - Tracks all received emails for audit purposes
 */
export const InboundEmailRecordSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),

  // Email details
  fromEmail: z.string().email(),
  toEmail: z.string().email(),
  subject: z.string().optional(),
  bodyHtml: z.string().optional(),
  bodyText: z.string().optional(),

  // Attachments processed
  attachments: z.array(z.object({
    filename: z.string(),
    contentType: z.string(),
    size: z.number(),
    r2Key: z.string(),
    contentHash: z.string(),
    documentId: z.string().uuid().optional(), // If processed into a document
  })).default([]),

  // Processing status
  status: z.enum(["RECEIVED", "PROCESSING", "PROCESSED", "FAILED", "REJECTED"]),
  processedAt: z.string().datetime().optional(),
  errorMessage: z.string().optional(),

  // Related workflow
  workflowTraceId: z.string().uuid().optional(),

  // Raw email storage (for compliance)
  rawEmailR2Key: z.string().optional(), // Full RFC822 email stored in R2

  // Compliance archive links (separate from mailbox folder archive)
  rawArchiveEntryId: z.string().uuid().optional(),
  archiveEntryIds: z.array(z.string().uuid()).default([]),
  complianceArchived: z.boolean().default(false),

  // Audit
  receivedAt: z.string().datetime(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type InboundEmailRecord = z.infer<typeof InboundEmailRecordSchema>;
